import React, { useEffect, useState } from 'react';
import Fallback from 'commons/components/fall-back';
import { Box, Flex, Text } from '@sparrowengg/twigs-react';
import { Spinner } from 'commons/components/spinner';
import CodeIcon from 'commons/icons/code';
import CodeEditor from 'features/code-editor/components';
import { devPortalServices } from 'commons/services';
import { EventsType, FieldType } from 'features/code-editor/types';
import { useDispatch } from 'react-redux';
import {
  clearCodeEditor,
  setAppValidation,
  setCurrentApp,
  setDependencies,
  setEventFields,
  setIparamApp,
  setOauthApp,
  setWhitelistedDomains
} from 'redux-store/actions';
import { useSearchParams } from 'react-router-dom';
import axios from 'lib/axios';
import { getHeader, setLocalStorageValue } from 'commons/helper';
import { IRootDispatch } from 'commons/types';
import { SingleAppDTOType } from 'features/dashboard/types';
import {
  SHOW_TOUR,
  booleanStringValues,
  productConstants
} from 'commons/constants';
import { sidebarItems } from 'features/code-editor/constants';

const CodeEditorPage = () => {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<Array<EventsType>>([]);
  const [currentFetchedEvent, setCurrentFetchedEvent] = useState<FieldType>();
  const dispatch: IRootDispatch = useDispatch();
  const [searchParams] = useSearchParams();

  function extractEventHandlers(codeString: string) {
    const eventHandlers: { [key: string]: string } = {};

    // Remove exports wrapper and surrounding braces
    codeString = codeString.replace(/^exports\s*=\s*{|}$/g, '').trim();

    // Regular expression to find function declarations
    const functionRegex = /(\w+)\s*:\s*async\s*function\s*\([^)]*\)\s*{/g;

    let match;
    while ((match = functionRegex.exec(codeString)) !== null) {
      const functionName = match[1];
      const startIndex = match.index + match[0].length;

      // Find the matching closing brace
      let bracketCount = 1;
      let endIndex = startIndex;
      let inString = false;
      let stringDelimiter = null;

      while (endIndex < codeString.length && bracketCount > 0) {
        const char = codeString[endIndex];

        if (!inString && (char === '"' || char === "'" || char === '`')) {
          inString = true;
          stringDelimiter = char;
        } else if (inString) {
          if (char === stringDelimiter && codeString[endIndex - 1] !== '\\') {
            inString = false;
            stringDelimiter = null;
          }
        } else {
          // Count brackets when not in a string
          if (char === '{') bracketCount++;
          if (char === '}') bracketCount--;
        }

        endIndex++;
      }

      if (bracketCount === 0) {
        const functionBody = codeString
          .slice(startIndex, endIndex - 1)
          .trim()
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line)
          .join('\n');

        eventHandlers[functionName] = functionBody;
      }
    }

    return eventHandlers;
  }

  const getAllEvents = async () => {
    try {
      const response = (await devPortalServices.getAllWebhookEvents())?.data;
      setEvents(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetCodeEditor = () => {
    dispatch(clearCodeEditor());
  };

  function transformIparams(iparams: {}) {
    return Object.entries(iparams).map(
      ([keyName, value]: [string, any], index) => {
        return {
          displayName: value.display_name,
          keyName: keyName,
          type: {
            label: value.type.toUpperCase(),
            value: value.type
          },
          description: value.description,
          secure: {
            label: value.secure
              ? booleanStringValues.TRUE
              : booleanStringValues.FALSE,
            value: value.secure
          },
          required: {
            label: value.required
              ? booleanStringValues.TRUE
              : booleanStringValues.FALSE,
            value: value.required
          },
          product: {
            label:
              value.type_attributes?.product === productConstants.SURVEYSPARROW
                ? productConstants.SURVEYSPARROW_CAPS
                : value.type_attributes?.product,
            value:
              value.type_attributes?.product === productConstants.SURVEYSPARROW
                ? productConstants.SURVEYSPARROW_CAPS
                : value.type_attributes?.product
          },
          id: index + 1
        };
      }
    );
  }

  const handleCurrentAppDetails = (response: SingleAppDTOType) => {
    dispatch(
      setCurrentApp({
        appId: response?.appId,
        appVersionId: response?.appVersionId
      })
    );
    const oauth: any = response?.oauthConfigDTO;
    !!response?.iparams &&
      dispatch(setIparamApp(transformIparams(response?.iparams)));
    !!oauth &&
      dispatch(setOauthApp({
        clientId: oauth.client_id,
        clientSecret: oauth.client_secret,
        authorizationUrl: oauth.authorize_url,
        tokenUrl: oauth.token_url,
        scopes: oauth?.options?.scope,
        pkce: oauth?.options?.pkce,
        codeVerifier: oauth?.options?.code_verifier,
        codeChallengeMethod: oauth?.options?.code_challenge_method
      }));

    dispatch(
      setWhitelistedDomains(
        response?.appManifestDTO?.['whitelisted-domains']?.map(
          (domain: string, index: number) => ({ id: index + 1, domain })
        ) ?? []
      )
    );
    dispatch(
      setDependencies(
        Object.entries(response?.appManifestDTO?.dependencies ?? {}).map(
          ([key, value], index) => ({
            id: index + 1,
            dependency: key,
            version: value
          })
        )
      )
    );
    const eventHandlers =
      extractEventHandlers(`${response?.files[0]?.fileContent}`) ?? {};
    const formattedEventHandlers = Object.entries(eventHandlers).map(
      ([field, code]) => ({
        type: sidebarItems.EVENT_HANDLER,
        field,
        code
      })
    );
    if (Object.values(eventHandlers)?.length) {
      setCurrentFetchedEvent(formattedEventHandlers[0]);
    }
    dispatch(setEventFields(formattedEventHandlers));
    dispatch(setAppValidation({
      isValidated: true,
      message: ""
    }))
  };

  const fetchCurrentAppDetails = async (appVersionId: string) => {
    try {
      const response: Partial<SingleAppDTOType> = (
        await axios.get(
          `/ssdk/template?appVersionId=${appVersionId}`,
          getHeader()
        )
      ).data;
      if (response?.appId) {
        handleCurrentAppDetails(response as SingleAppDTOType);
      }
      setLocalStorageValue(SHOW_TOUR, booleanStringValues.FALSE);
    } catch (error) {
      console.error(error);
    } finally {
      getAllEvents();
    }
  };

  useEffect(() => {
    const appVersionId = searchParams.get('app');
    resetCodeEditor();
    if (!!appVersionId) {
      fetchCurrentAppDetails(appVersionId);
      return;
    }
    getAllEvents();
  }, []);

  return (
    <Box
      css={{
        height: '100vh',
        background: '$black900',
        color: '$white900'
      }}
    >
      {loading ? (
        <Flex
          alignItems="center"
          justifyContent="center"
          css={{ height: '100%' }}
        >
          <Flex flexDirection="column" gap="$30" alignItems="center">
            <Flex alignItems="center" gap="$4">
              <CodeIcon size={24} color="#56B0BB" />
              <Text size="lg" weight="medium" css={{ color: '$primary400' }}>
                AppNest Codespace
              </Text>
            </Flex>
            <Fallback
              icon={<Spinner size="md" />}
              description="Please wait while the AppNest Editor is assembling the required tools"
              title={
                !!searchParams.get('app')
                  ? 'Your app is loading!'
                  : 'Creating you app!'
              }
              isBlackTheme
              isLoader
            />
          </Flex>
        </Flex>
      ) : (
        <CodeEditor events={events} currentFetchedEvent={currentFetchedEvent} />
      )}
    </Box>
  );
};

export default CodeEditorPage;
